// @ts-nocheck

import React, { useCallback, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import ScrollShadow from '../../../../components/ScrollShadow';
import { getRedirectUrl, RedirectServices } from '../../../../config/urls';
import { getLang, __ } from '../../../../i18n/l10n';
import { Locales } from '../../../../types/common';
import { GiftRaceImgByLang } from '../../assets/img';
import { GRRulesCardboardImgPath } from '../../assets/img/rules';
import SharedStyle from '../../styled-component/shared';

const PARCHMENT_RATIO = 621 / 910;

const Wrapper = styled.div`
  position: relative;
  height: 100%;
  width: 100%;

  display: flex;
  justify-content: center;
  align-items: center;
`;

const Background = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;

  ${({ $pageRatio }) =>
    $pageRatio < PARCHMENT_RATIO
      ? css`
          width: 100%;
        `
      : css`
          height: 100%;
        `}
`;

const ContentWrapper = styled.div`
  position: absolute;

  ${({ $size }) => css`
    width: ${$size.width}px;
    height: ${$size.height}px;
  `}
`;

const Content = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  padding: 10%;
  padding-top: 20%;

  display: flex;
  flex-direction: column;
  justify-content: space-between;
  text-align: center;
  color: #000000d9;
`;

const RuleList = styled(ScrollShadow)`
  display: flex;
  flex-direction: column;
  flex: 1;
  align-items: center;

  & > * {
    padding: 1em;
  }
`;

const RuleListItem = styled.div`
  font-family: 'BettingTradeGothicLTStd';
  font-weight: bold;

  ::before {
    content: '• ';
  }

  & + & {
    margin-top: 1em;
  }
`;

const AllRulesButton = styled.div`
  margin-top: 1.5em;

  display: flex;
  justify-content: center;
  align-items: center;
`;

const AllRulesButtonLabel = styled.div`
  color: rgb(163, 38, 30);
  font-size: 1.3em;

  background-image: linear-gradient(to bottom, rgb(163, 38, 30), rgb(163, 38, 30));
  background-size: 100% 2px;
  background-position: left bottom;
  background-repeat: no-repeat;
  cursor: pointer;

  ${SharedStyle.ImageAsButtonsBehaviour}
`;

const GRLogo = styled.img`
  max-width: 100%;
  max-height: 30%;
  margin-bottom: 1em;
`;

type GRRuleProps = {
  className?: string;
  rules: Array<string>;
  openExternalLink: (...params: unknown) => unknown;
};

const GRRule = ({ className, rules, openExternalLink }: GRRuleProps) => {
  const [pageRatio, setPageRatio] = useState(0);
  const [backgroundElement, setBackgroundElement] = useState();
  const [backgroundSize, setBackgroundSize] = useState({ width: 0, height: 0 });

  const [isComponentReady, setIsComponentReady] = useState(false);

  const onAllRulesClick = useCallback(() => {
    openExternalLink(getRedirectUrl(RedirectServices.GR_ALL_RULES), { auth: true });
  }, [openExternalLink]);

  const measuredPageRatio = useCallback((node) => {
    if (node) {
      const { width, height } = node.getBoundingClientRect();
      setPageRatio(width / height);
    }
  }, []);

  useEffect(() => {
    if (pageRatio && backgroundElement) {
      let { width, height } = backgroundElement.getBoundingClientRect();

      // widht or height of getBoundingClientRect() is 0 if not set in css
      // https://www.w3.org/TR/cssom-view/#dom-element-clientheight
      if (width === 0) {
        width = height * PARCHMENT_RATIO;
      } else if (height === 0) {
        height = width * (1 / PARCHMENT_RATIO);
      }

      setBackgroundSize({ width, height });
      setIsComponentReady(true);
    }
  }, [pageRatio, backgroundElement]);

  return (
    <Wrapper ref={measuredPageRatio} className={className}>
      <Background ref={setBackgroundElement} src={GRRulesCardboardImgPath} $pageRatio={pageRatio} />
      <ContentWrapper $size={backgroundSize}>
        <Content>
          <GRLogo src={GiftRaceImgByLang[getLang() ?? Locales.EN]} />
          <RuleList isReady={isComponentReady}>
            {rules.map((rule, idx) => (
              <RuleListItem key={`rule-list-item-${idx}`}>{rule}</RuleListItem>
            ))}
            <AllRulesButton>
              <AllRulesButtonLabel onClick={onAllRulesClick}>{__('All rules')}</AllRulesButtonLabel>
            </AllRulesButton>
          </RuleList>
        </Content>
      </ContentWrapper>
    </Wrapper>
  );
};

export default GRRule;
